import { useIntl } from "react-intl";
import { PricingPlanComparePlansItemFragment } from "src/graphql-types";

import { SIGNUP_CTA_URL } from "@/components/wpl/pricing/src/price-finder/constants";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features";
import { Jfind1384PriceCard } from "@/components/wpl/pricing/src/price-finder-jfind1384/jfind-1384-price-card";
import { jiraOfferingSkus } from "@/components/wpl/pricing/src/price-finder-v3/configs/index.ts";
import { useSelector } from "@/components/wpl/pricing/src/price-finder-v3/hooks.ts";
import * as selectors from "@/components/wpl/pricing/src/price-finder-v3/selectors.ts";
import {
    usePricingPlanComparison,
    usePricingPlanCta
} from "@/components/wpl/pricing/src/pricing-content-context";
import { useLocalizedHref } from "@/libraries/web-framework";

import {
    DATA_PRODUCT_KEY,
    SignupUrlSuffix,
    PriceDisplayMessages
} from "./utils/constants";

export const PlanStandard = () => {
    const intl = useIntl();
    const billingCycle = useSelector(selectors.getBillingCycle);

    const isUnitCountAboveMaxThreshold = useSelector((ctx) =>
        selectors.isUnitCountAboveMaxThreshold(ctx, jiraOfferingSkus.standard)
    );

    let price = useSelector((ctx) =>
        selectors.getRoundedFormattedPlanPrice(ctx, jiraOfferingSkus.standard)
    );

    let priceDisplayMessage: ReactNode | undefined = undefined;
    let isCtaDisabled = false;
    if (isUnitCountAboveMaxThreshold) {
        isCtaDisabled = true;
        price = null;
        priceDisplayMessage = intl.formatMessage(
            PriceDisplayMessages.exceedsUserLimit
        );
    }

    const recommended = useSelector((ctx) =>
        selectors.isOfferingRecommended(ctx, jiraOfferingSkus.standard)
    );
    const unitCount = useSelector((ctx) => selectors.getUnitCount(ctx));
    const unitTierLowerBound = useSelector((ctx) =>
        selectors.getUnitTierLowerBound(ctx, jiraOfferingSkus.standard)
    );
    const unitTierUpperBound = useSelector((ctx) =>
        selectors.getUnitTierUpperBound(ctx, jiraOfferingSkus.standard)
    );

    // todo: localized strings (https://hello.jira.atlassian.cloud/browse/JFIND-1475)
    const standardContent: PricingPlanComparePlansItemFragment | undefined =
        usePricingPlanComparison("Standard");
    const standardPlanFeatureMessages = createPlanFeatureMessages(
        { standard: standardContent },
        "standard"
    );
    const getPricingPlanCta = usePricingPlanCta();
    const contentfulCta = getPricingPlanCta({ planName: "Standard" });
    const ctaText = intl.formatMessage({
        id: "Pricing.PriceCard.Premium.CtaText",
        defaultMessage: "Get it free"
    });
    const localizedSignupURL = useLocalizedHref(SIGNUP_CTA_URL);
    const ctaUrl = `${localizedSignupURL}${SignupUrlSuffix["standard"]}`;

    const planDescription = intl.formatMessage({
        id: "Pricing.PriceCard.Standard.Description",
        defaultMessage:
            "For teams needing better security controls and automation"
    });

    const freeTag = price && billingCycle === "MONTHLY" ? "14days" : undefined;
    const priceSlash =
        price && billingCycle === "MONTHLY" ? "yellow" : undefined;

    const numberFormatLocale = useSelector(selectors.getNumberFormatLocale);

    return (
        <Jfind1384PriceCard
            highlighted={recommended}
            isRecommendedPlan={recommended}
            planName="Standard"
            description={planDescription}
            planFeatureMessages={standardPlanFeatureMessages}
            ctaId={"planCtaStandard"}
            ctaText={ctaText}
            ctaUrl={contentfulCta?.ctaUrl ?? ctaUrl}
            isCtaDisabled={isCtaDisabled}
            billingCycle={billingCycle}
            dataProductKey={DATA_PRODUCT_KEY}
            price={price}
            priceDisplayMessage={priceDisplayMessage}
            priceCalcIdx={0}
            edition="standard"
            freeTag={freeTag}
            priceSlash={priceSlash}
            adjacentLowerTierPlanName={"Free"}
            numberFormatLocale={numberFormatLocale}
            unitCount={unitCount}
            unitTierLowerBound={unitTierLowerBound}
            unitTierUpperBound={unitTierUpperBound}
        />
    );
};
